import React, { useState, useEffect } from 'react';
import { Brain, ChevronDown, Download } from 'lucide-react';

export default function LandingPage() {
    const [typedText, setTypedText] = useState('');
    const [showArrow1, setShowArrow1] = useState(false);
    const [showVortex, setShowVortex] = useState(false);
    const [showArrow2, setShowArrow2] = useState(false);
    const [showFolder, setShowFolder] = useState(false);
    const [vortexBinary, setVortexBinary] = useState([]);
    const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
    const [visitorIP, setVisitorIP] = useState('10.0.0.4');
    const [loginTime, setLoginTime] = useState('');
    const [showBootMessage, setShowBootMessage] = useState(false);
    const [showTyping, setShowTyping] = useState(false);

    const scenarios = [
        {
            text: 'make me "Hello World app"',
            filename: 'hello_world.py',
            code: `#!/usr/bin/env python3
# Generated by Lead Software Developer

def main():
    print("Hello World")

if __name__ == "__main__":
    main()
`
        },
        {
            text: 'build me "Weather app"',
            filename: 'weather_app.py',
            code: `#!/usr/bin/env python3
# Generated by Lead Software Developer

import requests
import json

def get_weather(city):
    api_key = "your_api_key_here"
    url = f"http://api.openweathermap.org/data/2.5/weather?q={city}&appid={api_key}&units=metric"
    
    response = requests.get(url)
    data = response.json()
    
    if response.status_code == 200:
        temp = data['main']['temp']
        description = data['weather'][0]['description']
        print(f"Weather in {city}: {temp}°C, {description}")
    else:
        print("City not found!")

if __name__ == "__main__":
    city = input("Enter city name: ")
    get_weather(city)
`
        },
        {
            text: 'create "Todo List app"',
            filename: 'todo_app.py',
            code: `#!/usr/bin/env python3
# Generated by Lead Software Developer

class TodoApp:
    def __init__(self):
        self.todos = []
    
    def add_todo(self, task):
        self.todos.append({"task": task, "completed": False})
        print(f"Added: {task}")
    
    def complete_todo(self, index):
        if 0 <= index < len(self.todos):
            self.todos[index]["completed"] = True
            print(f"Completed: {self.todos[index]['task']}")
    
    def show_todos(self):
        for i, todo in enumerate(self.todos):
            status = "✓" if todo["completed"] else "○"
            print(f"{i+1}. {status} {todo['task']}")

if __name__ == "__main__":
    app = TodoApp()
    app.add_todo("Learn Python")
    app.add_todo("Build an app")
    app.show_todos()
`
        },
        {
            text: 'develop "Calculator app"',
            filename: 'calculator.py',
            code: `#!/usr/bin/env python3
# Generated by Lead Software Developer

class Calculator:
    def add(self, a, b):
        return a + b
    
    def subtract(self, a, b):
        return a - b
    
    def multiply(self, a, b):
        return a * b
    
    def divide(self, a, b):
        if b != 0:
            return a / b
        return "Error: Division by zero"
    
    def run(self):
        print("Simple Calculator")
        print("Operations: +, -, *, /")
        
        while True:
            try:
                num1 = float(input("Enter first number: "))
                op = input("Enter operation (+, -, *, /): ")
                num2 = float(input("Enter second number: "))
                
                if op == '+':
                    result = self.add(num1, num2)
                elif op == '-':
                    result = self.subtract(num1, num2)
                elif op == '*':
                    result = self.multiply(num1, num2)
                elif op == '/':
                    result = self.divide(num1, num2)
                else:
                    print("Invalid operation")
                    continue
                
                print(f"Result: {result}")
                
                if input("Continue? (y/n): ").lower() != 'y':
                    break
            except ValueError:
                print("Invalid input")

if __name__ == "__main__":
    calc = Calculator()
    calc.run()
`
        },
        {
            text: 'generate "API server"',
            filename: 'api_server.py',
            code: `#!/usr/bin/env python3
# Generated by Lead Software Developer

from flask import Flask, jsonify, request

app = Flask(__name__)

# Sample data
users = [
    {"id": 1, "name": "Alice", "email": "alice@example.com"},
    {"id": 2, "name": "Bob", "email": "bob@example.com"}
]

@app.route('/api/users', methods=['GET'])
def get_users():
    return jsonify(users)

@app.route('/api/users/<int:user_id>', methods=['GET'])
def get_user(user_id):
    user = next((u for u in users if u['id'] == user_id), None)
    if user:
        return jsonify(user)
    return jsonify({"error": "User not found"}), 404

@app.route('/api/users', methods=['POST'])
def create_user():
    data = request.json
    new_user = {
        "id": len(users) + 1,
        "name": data.get("name"),
        "email": data.get("email")
    }
    users.append(new_user)
    return jsonify(new_user), 201

if __name__ == "__main__":
    app.run(debug=True, port=5000)
    print("API server running on http://localhost:5000")
`
        }
    ];

    const currentScenario = scenarios[currentScenarioIndex];

    // Get visitor IP and set login time on component mount
    useEffect(() => {
        // Set login time with proper format
        const now = new Date();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const timeString = `${dayNames[now.getDay()]} ${monthNames[now.getMonth()]} ${now.getDate().toString().padStart(2, '0')} ${now.toTimeString().split(' ')[0]} ${now.getFullYear()}`;
        setLoginTime(timeString);

        // Get visitor IP
        fetch('https://api.ipify.org?format=json')
            .then(response => response.json())
            .then(data => setVisitorIP(data.ip))
            .catch(() => setVisitorIP('10.0.0.4')); // fallback IP if fetch fails

        // Show boot message after a short delay
        setTimeout(() => setShowBootMessage(true), 500);
        setTimeout(() => setShowTyping(true), 1500);
    }, []);

    // Typing animation with scenario cycling
    useEffect(() => {
        if (!showTyping) return;

        let currentIndex = 0;
        const currentText = currentScenario.text;

        // Reset states for new scenario
        setTypedText('');
        setShowArrow1(false);
        setShowVortex(false);
        setShowArrow2(false);
        setShowFolder(false);

        const typingInterval = setInterval(() => {
            if (currentIndex < currentText.length) {
                setTypedText(currentText.slice(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingInterval);
                setTimeout(() => setShowArrow1(true), 500);
                setTimeout(() => setShowVortex(true), 1500);
                setTimeout(() => setShowArrow2(true), 3000);
                setTimeout(() => setShowFolder(true), 4000);

                // Wait for full animation, then cycle to next scenario
                setTimeout(() => {
                    setCurrentScenarioIndex((prev) => (prev + 1) % scenarios.length);
                }, 8000);
            }
        }, 100);

        return () => clearInterval(typingInterval);
    }, [currentScenarioIndex, scenarios.length, currentScenario.text, showTyping]);

    // Generate binary vortex
    useEffect(() => {
        const generateBinary = () => {
            const binary = [];
            for (let i = 0; i < 50; i++) {
                const angle = (i / 50) * Math.PI * 2;
                const radius = 20 + (i % 5) * 15;
                binary.push({
                    value: Math.random() > 0.5 ? '1' : '0',
                    x: 50 + Math.cos(angle) * radius,
                    y: 50 + Math.sin(angle) * radius,
                    delay: (i % 10) * 0.2,
                    scale: 0.5 + Math.random() * 1,
                    opacity: 0.3 + Math.random() * 0.7
                });
            }
            setVortexBinary(binary);
        };

        if (showVortex) {
            generateBinary();
            const interval = setInterval(generateBinary, 3000);
            return () => clearInterval(interval);
        }
    }, [showVortex]);

    // Download functionality
    const handleDownload = () => {
        const blob = new Blob([currentScenario.code], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = currentScenario.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Email functionality with obfuscation
    const getEmail = () => {
        // Obfuscated email parts
        const user = 'ZWQ='; // base64 encoded 'ed'
        const domain = 'bGVhZHNvZnR3YXJlZGV2ZWxvcGVy'; // base64 encoded 'leadsoftwaredeveloper'
        const tld = 'Y29t'; // base64 encoded 'com'

        return atob(user) + '@' + atob(domain) + '.' + atob(tld);
    };

    const handleGetStarted = () => {
        const email = getEmail();
        const subject = encodeURIComponent('Get Started with LeadSoftwareDeveloper');
        const body = encodeURIComponent('Hi Ed,\n\nI\'m interested in getting started with LeadSoftwareDeveloper. Could you please provide more information?\n\nThanks!');
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    };

    const handleEarlyAccess = () => {
        const email = getEmail();
        const subject = encodeURIComponent('Early Access Request for LeadSoftwareDeveloper');
        const body = encodeURIComponent('Hi Ed,\n\nI would like to request early access to LeadSoftwareDeveloper. I\'m excited to try this revolutionary development tool!\n\nPlease let me know the next steps.\n\nThanks!');
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white overflow-hidden">
            {/* Background animated elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
            </div>

            {/* Main content */}
            <div className="relative z-10">
                {/* Navigation */}
                <nav className="flex justify-between items-center px-4 sm:px-8 py-6">
                    <div className="flex items-center space-x-2">
                        <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                        <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent flex-shrink-0">
                            LeadSoftwareDeveloper
                        </div>
                    </div>
                    <button
                        onClick={handleGetStarted}
                        className="px-3 sm:px-6 py-2 text-sm sm:text-base bg-white bg-opacity-10 backdrop-blur-md rounded-full hover:bg-opacity-20 transition-all duration-300 border border-white border-opacity-20 whitespace-nowrap ml-2"
                    >
                        Get Started
                    </button>
                </nav>

                {/* Hero Section */}
                <div className="container mx-auto px-8 py-16">
                    <div className="text-center mb-16">
                        <div className="flex justify-center mb-6">
                        </div>
                        <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text text-transparent animate-gradient">
                            Transform Ideas Into Reality
                        </h1>
                        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                            The next generation of software development. Watch your thoughts become applications in seconds.
                        </p>
                    </div>

                    {/* Interactive Demo */}
                    <div className="max-w-4xl mx-auto">
                        {/* Terminal */}
                        <div className="bg-black bg-opacity-50 backdrop-blur-md rounded-lg p-6 border border-gray-700 shadow-2xl transition-all duration-300">
                            <div className="flex items-center mb-4">
                                <div className="flex space-x-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <div className="ml-4 text-sm text-gray-400">Terminal - LeadSoftwareDeveloper v1.0 alpha</div>
                            </div>
                            <div className="font-mono text-sm">
                                {showBootMessage && (
                                    <div className="text-gray-300 mb-4 animate-fadeIn">
                                        <div className="flex items-center mb-2">
                                            <span className="text-orange-400"></span>
                                            <span className="ml-2">Welcome to Ubuntu 24.04.2 LTS (GNU/Linux 6.8.0-60-lowlatency x86_64)</span>
                                        </div>
                                        <div className="pl-4 space-y-1 text-gray-400">
                                            <div>* Documentation: <a href="https://help.ubuntu.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline transition-colors">https://help.ubuntu.com</a></div>
                                            <div>* Management: <a href="https://landscape.canonical.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline transition-colors">https://landscape.canonical.com</a></div>
                                            <div>* Support: <a href="https://ubuntu.com/pro" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline transition-colors">https://ubuntu.com/pro</a></div>
                                        </div>
                                        <div className="mt-2 text-gray-400">
                                            Expanded Security Maintenance for Applications is enabled.
                                        </div>
                                        <div className="mt-2 text-gray-300">
                                            Last login: {loginTime} from {visitorIP}
                                        </div>
                                        <div className="mt-2">
                                            <span className="text-green-400">user@vortex</span>:<span className="text-blue-400">~</span>$
                                        </div>
                                    </div>
                                )}
                                {showTyping && (
                                    <div>
                                        <span className="text-green-400">user@vortex</span>:<span className="text-blue-400">~</span>$
                                        <span className="ml-2 text-white">{typedText}</span>
                                        <span className="animate-pulse">|</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Scenario indicators */}
                        <div className="flex justify-center mt-4 space-x-2">
                            {scenarios.map((_, index) => (
                                <div
                                    key={index}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        index === currentScenarioIndex
                                            ? 'bg-blue-400 w-8'
                                            : 'bg-gray-500'
                                    }`}
                                ></div>
                            ))}
                        </div>

                        {/* Current scenario type indicator */}
                        <div className="text-center mt-3">
                            <span className="text-sm text-blue-300 opacity-70">
                                Generating: {currentScenario.filename.replace('.py', '').replace('_', ' ').toUpperCase()}
                            </span>
                        </div>

                        {/* Arrow 1 */}
                        {showArrow1 && (
                            <div className="flex justify-center my-8">
                                <ChevronDown className="w-12 h-12 text-blue-400 animate-bounce" />
                            </div>
                        )}

                        {/* Vortex */}
                        {showVortex && (
                            <div className="relative h-64 flex items-center justify-center">
                                {/* Outer rotating ring */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-48 h-48 rounded-full border-2 border-blue-400 border-opacity-20 animate-spin-slow"></div>
                                </div>

                                {/* Middle rotating ring - opposite direction */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-36 h-36 rounded-full border-2 border-purple-400 border-opacity-30 animate-spin-reverse"></div>
                                </div>

                                {/* Inner rotating ring */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-24 h-24 rounded-full border-2 border-pink-400 border-opacity-40 animate-spin-slow"></div>
                                </div>

                                {/* Binary vortex */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-48 h-48 relative animate-spin-slow">
                                        {vortexBinary.map((bit, index) => (
                                            <span
                                                key={index}
                                                className="absolute text-xl font-mono animate-pulse transform transition-all duration-1000"
                                                style={{
                                                    left: `${bit.x}%`,
                                                    top: `${bit.y}%`,
                                                    transform: `translate(-50%, -50%) scale(${bit.scale})`,
                                                    animationDelay: `${bit.delay}s`,
                                                    opacity: bit.opacity,
                                                    color: index % 3 === 0 ? '#60A5FA' : index % 3 === 1 ? '#C084FC' : '#F472B6'
                                                }}
                                            >
                        {bit.value}
                      </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Center glow effect */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-xl opacity-50 animate-pulse"></div>
                                </div>
                            </div>
                        )}

                        {/* Arrow 2 */}
                        {showArrow2 && (
                            <div className="flex justify-center my-8">
                                <ChevronDown className="w-12 h-12 text-purple-400 animate-bounce" />
                            </div>
                        )}

                        {/* Download Folder */}
                        {showFolder && (
                            <div className="flex justify-center">
                                <button
                                    onClick={handleDownload}
                                    className="group relative bg-gradient-to-r from-blue-500 to-purple-500 p-8 rounded-2xl hover:scale-105 transition-all duration-300 shadow-2xl"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-40 group-hover:opacity-60 transition-opacity"></div>
                                    <div className="relative flex flex-col items-center space-y-4">
                                        <Download className="w-16 h-16 text-white animate-pulse" />
                                        <span className="text-lg font-semibold">{currentScenario.filename}</span>
                                        <span className="text-sm text-gray-200">Click to download</span>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Features */}
                    <div className="grid md:grid-cols-3 gap-8 mt-24">
                        <div className="bg-white bg-opacity-5 backdrop-blur-md rounded-xl p-6 border border-white border-opacity-10 hover:bg-opacity-10 transition-all duration-300">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                                <span className="text-2xl">⚡</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
                            <p className="text-gray-300">Transform your ideas into working applications in seconds, not hours.</p>
                        </div>

                        <div className="bg-white bg-opacity-5 backdrop-blur-md rounded-xl p-6 border border-white border-opacity-10 hover:bg-opacity-10 transition-all duration-300">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                                <span className="text-2xl">🧠</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-2">AI-Powered</h3>
                            <p className="text-gray-300">Advanced intelligence that understands your intent and delivers exactly what you need.</p>
                        </div>

                        <div className="bg-white bg-opacity-5 backdrop-blur-md rounded-xl p-6 border border-white border-opacity-10 hover:bg-opacity-10 transition-all duration-300">
                            <div className="w-12 h-12 bg-gradient-to-r from-pink-400 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                                <span className="text-2xl">🔒</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Enterprise Ready</h3>
                            <p className="text-gray-300">Secure, scalable, and built for the most demanding professional environments.</p>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="text-center mt-24">
                        <h2 className="text-3xl font-bold mb-4">Ready to revolutionize your development?</h2>
                        <p className="text-gray-300 mb-8">Join the waitlist and be among the first to experience the future.</p>
                        <button
                            onClick={handleEarlyAccess}
                            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-2xl"
                        >
                            Request Early Access
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes gradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }

                .animate-gradient {
                    background-size: 200% 200%;
                    animation: gradient 3s ease infinite;
                }

                .animate-spin-slow {
                    animation: spin 10s linear infinite;
                }

                .animate-spin-reverse {
                    animation: spin-reverse 8s linear infinite;
                }

                .animate-spin-reverse-slow {
                    animation: spin-reverse 15s linear infinite;
                }

                .animation-delay-2000 {
                    animation-delay: 2s;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                @keyframes spin-reverse {
                    from { transform: rotate(360deg); }
                    to { transform: rotate(0deg); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-in;
                }
            `}</style>
        </div>
    );
}